"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";

import { PixelEffect } from "@/components/ui/pixel-effect-dynamic";

import { Button } from "@/components/ui/button";
import Breadcrumbs from "@/components/Breadcrumbs";
import HighlightedText from "@/components/ui/highlighted-text";
import { tinaFieldSafe, cn } from "@/utils";

import { useTransform, motion, useScroll, useReducedMotion } from "motion/react";

export interface HeaderButton {
  label: string;
  link?: string;
  variant?: "default" | "outline" | "link";
  size?: "default" | "sm" | "lg" | "icon";
}

interface HeaderProps {
  title?: string;
  subtitle?: string | any;
  /** Tina field name for inline editing of `subtitle` (default `"subtitle"`; e.g. video pages use `"description"`). */
  subtitleField?: string;
  /** Tina field name for inline editing of `title` (default `"title"`; e.g. team pages use `"name"`). */
  titleField?: string;
  pretitle?: string;
  pretitleField?: string; // Optional field name to map pretitle to (e.g., "publishDate")
  /** Precomputed `data-tina-field` for title (e.g. nested about team member paths). */
  titleDataTinaField?: string;
  /** Precomputed `data-tina-field` for subtitle. */
  subtitleDataTinaField?: string;
  button?: HeaderButton;
  children?: React.ReactNode;
  /** Right-aligned header actions (e.g. RSS subscribe), shown beside the title. */
  actions?: React.ReactNode;
  wrapperClassName?: string;
  className?: string;
  variant?: "center" | "left";
  backgroundVariant?:
  | "pixels-bl-tr"
  | "pixels-l-r"
  | "pixels-subtle"
  | "pixels-bl-tr-2"
  | "none";
  _content_source?: any;
  /** Background video. When set, it replaces the pixel background. */
  backgroundVideo?: string;
  backgroundVideoClassName?: string;
  /** Poster for the video. Used alone as the background when no video is set. */
  backgroundPoster?: string;
  /** Show the breadcrumb trail. Defaults to true. */
  showBreadcrumbs?: boolean;
  /** Optional image layered over the background effect (e.g. blog post hero). */
  backgroundImage?: string;
  backgroundImageField?: string;
}

// Dispatcher: chooses a variant-specific component. By splitting the
// implementations we ensure `useScroll`/`useTransform` are ONLY subscribed when
// the "center" variant actually renders. Previously the parent always called
// `useScroll()` and 4× `useTransform()` even for the "left" variant — and the
// "left" variant is used by every non-homepage route, so the scroll
// subscription ran on every page.
export default function Header(props: HeaderProps) {
  if (props.variant === "center") {
    return <HeaderCenter {...props} />;
  }
  return <HeaderLeft {...props} />;
}

function HeaderLeft({
  title,
  subtitle,
  subtitleField = "subtitle",
  titleField = "title",
  pretitle,
  pretitleField,
  button,
  titleDataTinaField,
  subtitleDataTinaField,
  children,
  actions,
  backgroundVariant = "pixels-bl-tr-2",
  wrapperClassName,
  className,
  _content_source,
  backgroundImage,
  backgroundImageField = "backgroundImage",
  showBreadcrumbs = true,
}: HeaderProps) {
  return (
    <header
      className={cn(
        "relative overflow-hidden -mt-[73px] pb-12  !pt-32 lg:!pt-40",
        wrapperClassName,
        {
          "bg-background-light": backgroundVariant === "none",
        }
      )}
    >
      {backgroundVariant === "pixels-bl-tr-2" && <BackgroundEffect2 />}
      {backgroundVariant === "pixels-l-r" && <BackgroundEffectLR />}
      {backgroundVariant === "pixels-subtle" && <BackgroundEffectSubtle />}
      {backgroundImage && (
        <>
          <div
            className="absolute inset-0 z-[1] pointer-events-none"
            aria-hidden="true"
            data-tina-field={tinaFieldSafe(
              { _content_source },
              backgroundImageField
            )}
          >
            <Image
              src={backgroundImage}
              alt=""
              fill
              className="object-cover object-right-center"
              sizes="100vw"
              priority
            />
          </div>
          <div
            className="absolute inset-0 z-[2] pointer-events-none md:hidden bg-gradient-to-b from-background/90 via-background/70 to-background/40"
            aria-hidden="true"
          />
        </>
      )}
      <div
        className={cn(
          "content  relative z-10 flex flex-col items-start gap-10",
          className
        )}
      >
        {(title || subtitle || pretitle || actions) ? (
          <div className="flex w-full min-w-0 flex-col gap-0">
            {showBreadcrumbs && (
              <Breadcrumbs className="-translate-y-6 lg:-translate-y-8" />
            )}
            <div className="space-y-2 w-full min-w-0">
              {pretitle && (
                <div
                  className="font-mono text-primary-200 uppercase"
                  data-tina-field={tinaFieldSafe(
                    { _content_source },
                    pretitleField || "pretitle"
                  )}
                >
                  {pretitle}
                </div>
              )}
              {(title || actions) && (
                <div className="flex w-full flex-row items-center justify-between gap-4">
                  {title && (
                    <h1
                      className="h1 text-balance min-w-0 max-w-5xl"
                      data-tina-field={
                        titleDataTinaField ??
                        tinaFieldSafe({ _content_source }, titleField)
                      }
                    >
                      {title}
                    </h1>
                  )}
                  {actions && <div className="shrink-0">{actions}</div>}
                </div>
              )}
              {subtitle && (
                <p
                  className="h5 !max-w-[1040px] text-balance !text-muted-foreground-light whitespace-pre-wrap"
                  data-tina-field={
                    subtitleDataTinaField ??
                    tinaFieldSafe({ _content_source }, subtitleField)
                  }
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
        ) : (
          showBreadcrumbs && (
            <Breadcrumbs className="-translate-y-6 lg:-translate-y-8" />
          )
        )}
        {button?.label && (
          <Button
            href={button?.link}
            {...(_content_source
              ? {
                "data-tina-field": tinaFieldSafe(
                  { _content_source },
                  "button.label"
                ),
              }
              : {})}
          >
            {button?.label}
          </Button>
        )}
        {children && children}
      </div>
    </header>
  );
}

function HeaderCenter({
  title,
  subtitle,
  subtitleField = "subtitle",
  titleField = "title",
  button,
  children,
  backgroundVariant,
  className,
  _content_source,
  backgroundVideo,
  backgroundPoster,
  backgroundVideoClassName,
  showBreadcrumbs = true,
}: HeaderProps) {
  const { scrollY } = useScroll();
  const [isMobile, setIsMobile] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const y = useTransform(scrollY, [0, 1000], [0, -250]);
  const y2 = useTransform(scrollY, [0, 1000], [0, -285]);
  const y3 = useTransform(scrollY, [0, 1000], [0, -333]);
  const opacity = useTransform(scrollY, [0, 700], [1, 0]);

  const playVideo = Boolean(backgroundVideo) && !prefersReducedMotion;
  const showPosterImage = Boolean(backgroundPoster) && !playVideo;
  const showMedia = playVideo || showPosterImage || Boolean(backgroundVideo);

  return (
    <header className={cn("overflow-hidden relative", className)}>
      {playVideo && (
        <video
          src={backgroundVideo}
          poster={backgroundPoster || undefined}
          preload="none"
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
          className={cn("absolute inset-0 w-full h-full z-0", backgroundVideoClassName)}
        />
      )}
      {showPosterImage && backgroundPoster && (
        <div
          className="absolute inset-0 z-0 pointer-events-none"
          aria-hidden="true"
        >
          <Image
            src={backgroundPoster}
            alt=""
            fill
            className="object-cover object-right-bottom"
            sizes="100vw"
            priority
          />
        </div>
      )}
      {showMedia && (
        <div className="absolute inset-0 w-full h-full bg-black/70 z-0" />
      )}
      <div className="content py-16 md:py-32 text-center">
        {!showMedia && backgroundVariant === "pixels-bl-tr" && (
          <BackgroundEffect />
        )}
        {!showMedia && (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-40 bg-gradient-to-b from-black/80 via-black/45 to-transparent md:hidden"
            aria-hidden="true"
          />
        )}
        <div className="relative z-10 space-y-4 md:space-y-8">

          {showBreadcrumbs && (
            <Breadcrumbs
              tone={showMedia ? "default" : "light"}
              className="flex justify-center items-center"
            />
          )}
          <motion.h1
            className="h0 max-w-[1200px] !mx-auto z-10 relative text-balance"
            data-tina-field={tinaFieldSafe({ _content_source }, titleField)}
            style={isMobile ? { opacity } : { y, opacity }}
          >
            {title}
          </motion.h1>
          <motion.div style={isMobile ? { opacity } : { y: y2, opacity }}>
            <HighlightedText
              as="h2"
              className="h4 max-w-[840px] mx-auto text-white !text-center"
              text={subtitle || ""}
              data-tina-field={tinaFieldSafe(
                { _content_source },
                subtitleField
              )}
            />
          </motion.div>
          {button?.label && (
            <motion.div style={isMobile ? { opacity } : { y: y3, opacity }}>
              <Button
                href={button?.link}
                data-tina-field={tinaFieldSafe(
                  { _content_source },
                  "button.label"
                )}
              >
                {button?.label}
              </Button>
            </motion.div>
          )}
          {children && <div className="mt-8">{children}</div>}
        </div>
      </div>
    </header>
  );
}

function BackgroundEffectLR() {
  return (
    <div className="absolute inset-0 w-full h-full">
      <PixelEffect
        animationSpeed={0.1}
        containerClassName="bg-black"
        colors={[
          [78, 64, 255],
          [10, 255, 199],
        ]}
        dotSize={3}
        opacities={[0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1]}
        showGradient={false}
        shader={`
        float animation_speed_factor = 100.0;
        float intro_offset = distance(u_resolution / 2.0 / u_total_size, st2) * 0.01 + (random(st2) * 0.15);
        opacity *= step(intro_offset, u_time * animation_speed_factor);
        opacity *= clamp((1.0 - step(intro_offset + 0.1, u_time * animation_speed_factor)) * 1.25, 1.0, 1.25);
        
        // Horizontal gradient from left to right
        vec2 normalizedPos = st / u_resolution;
        float horizontalGradient = normalizedPos.x;
        float verticalGradient = normalizedPos.y;
        
        // Create three zones: dark (left), purple (middle), green (right)
        float zone1 = smoothstep(0.0, 0.2, horizontalGradient); // Dark to purple transition (faster for more purple on left)
        float zone2 = smoothstep(0.5, 0.95, horizontalGradient); // Purple to green transition (delayed for more purple)
        
        // Mix colors: start with dark, blend to purple, then to green
        vec3 darkColor = vec3(0.0, 0.0, 0.0);
        vec3 purpleColor = u_colors[0];
        vec3 greenColor = u_colors[3];
        
        color = mix(darkColor, purpleColor, zone1);
        color = mix(color, greenColor, zone2);
        
        // Reduce opacity on the left side to create darker effect, and slightly reduce on right for subtle green
        // Adjusted to show more purple earlier but keep it subtle
        float baseOpacity = mix(0.18, 0.85, horizontalGradient);
        
        // Boost purple visibility in the bottom area - very subtle
        float bottomPurpleBoost = smoothstep(0.3, 1.0, verticalGradient) * (1.0 - horizontalGradient);
        opacity *= baseOpacity + (bottomPurpleBoost * 0.2);
        `}
      />
      <div className="absolute z-0 w-full h-full bg-gradient-to-r from-black/0 via-black/0 via-45% to-black/20 top-0 left-0" />
    </div>
  );
}

function BackgroundEffect() {
  return (
    <>
      <div className="absolute z-0 w-[2000px] h-[1200px] top-0 -left-[1000px] overflow-hidden">
        <PixelEffect
          animationSpeed={1000}
          containerClassName="bg-black"
          colors={[[78, 64, 255]]}
          dotSize={3}
          opacities={[0.55, 0.65, 0.85, 0.95, 1]}
          showGradient={false}
        />
        <div className="absolute z-0 w-full h-full bg-gradient-to-r from-black/10 via-black/20 via-60% to-black top-0 left-0" />
        <div className="absolute z-0 w-full h-full bg-gradient-to-t from-black/10 via-black/20 via-50% to-black top-0 left-0" />
      </div>
      <div className="absolute z-0 w-[2000px] h-full -top-1/2 right-[-1000px] overflow-hidden">
        <PixelEffect
          animationSpeed={1000}
          containerClassName="bg-black"
          colors={[[10, 255, 199]]}
          dotSize={3}
          opacities={[0.55, 0.65, 0.75, 0.95, 1]}
          showGradient={false}
        />
        <div className="absolute z-0 w-full h-full bg-gradient-to-l from-black/0 via-black/10 via-60% to-black top-0 left-0" />
        <div className="absolute z-0 w-full h-full bg-gradient-to-b from-black/0 via-black/10 via-60% to-black top-0 left-0" />
      </div>
    </>
  );
}
function BackgroundEffect2() {
  return (
    <>
      <div className="absolute z-0 w-[50vw] h-full top-0 left-0 overflow-hidden">
        <PixelEffect
          animationSpeed={1000}
          containerClassName="bg-black"
          colors={[[78, 64, 255]]}
          dotSize={3}
          opacities={[0.55, 0.65, 0.85, 0.95, 1]}
          showGradient={false}
        />
        <div className="absolute z-0 w-full h-full bg-gradient-to-br from-black/40 via-black via-60% to-black top-0 left-0" />
      </div>
      <div className="absolute z-0 w-[100vw] h-full top-0 -right-[40%] overflow-hidden">
        <PixelEffect
          animationSpeed={1000}
          containerClassName="bg-black"
          colors={[[10, 255, 199]]}
          dotSize={3}
          opacities={[0.55, 0.65, 0.75, 0.95, 1]}
          showGradient={false}
        />
        <div className="absolute z-0 w-full h-full bg-gradient-to-l from-black/0 via-black/10 via-60% to-black top-0 left-0" />
        <div className="absolute z-0 w-full h-full bg-gradient-to-b from-black/0 via-black/40 via-0% to-black/95 top-0 left-0" />
      </div>
    </>
  );
}

function BackgroundEffectSubtle() {
  return (
    <div className="absolute inset-0 w-full h-full left-0 z-0">
      <PixelEffect
        containerClassName="bg-gradient-to-br from-[#4E40FF]/0 via-[#2D4A9A]/60 to-[#0AFFC7]/50"
        colors={[
          [78, 64, 255],
          [10, 255, 199],
        ]}
        dotSize={3}
        opacities={[0.9, 1.0, 1.0]}
        showGradient={false}
        shader={`
        float animation_speed_factor = 100.0;
        float intro_offset = distance(u_resolution / 2.0 / u_total_size, st2) * 0.01 + (random(st2) * 0.15);
        opacity *= step(intro_offset, u_time * animation_speed_factor);
        opacity *= clamp((1.0 - step(intro_offset + 0.1, u_time * animation_speed_factor)) * 1.25, 1.0, 1.25);
        
        // Horizontal gradient for left-to-right transition
        vec2 normalizedPos = st / u_resolution;
        float gradientFactor = normalizedPos.x;
        
        // Smooth horizontal transition
        float smoothGradient = smoothstep(0.0, 1.0, gradientFactor);
        
        color = mix(u_colors[0], u_colors[3], smoothGradient);
      `}
      />

      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-background/20 via-background/40 to-background" />
      <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-background/20 via-background/60 to-background" />
    </div>
  );
}
